/**
 * PermissionService - Runtime permission evaluation for RBAC
 *
 * Purpose: Centralized permission checking based on user role and company membership.
 * Used by guards, directives, and components for access control decisions.
 *
 * Roles (in order of privilege):
 * - super_admin: Full access across all companies (audit logged)
 * - admin: Full access within their company
 * - compliance_officer: Compliance management within their company
 * - user: Basic access within their company (default)
 *
 * Permissions are checked against:
 * 1. User's role (from user.role field)
 * 2. Company membership (user.currentCompanyId and user.companies[])
 * 3. Feature flags (for tier-based access, delegates to TierAccessService)
 */
import { Injectable, inject } from '@angular/core';
import { Observable, map, combineLatest } from 'rxjs';
import { AuthService } from './auth.service';
import { User } from '../models/user';

/**
 * Permission enum for fine-grained access control
 * Maps to specific actions users can perform
 */
export enum Permission {
  // Company Management
  MANAGE_COMPANY = 'manage_company',
  DELETE_COMPANY = 'delete_company',

  // User Management
  MANAGE_USERS = 'manage_users',
  INVITE_USERS = 'invite_users',
  DELETE_USERS = 'delete_users',

  // Scan Management
  MANAGE_SCANS = 'manage_scans',
  RUN_SCANS = 'run_scans',
  DELETE_SCANS = 'delete_scans',

  // Asset Management
  MANAGE_ASSETS = 'manage_assets',
  DELETE_ASSETS = 'delete_assets',

  // Vulnerability Management
  MANAGE_VULNERABILITIES = 'manage_vulnerabilities',
  DELETE_VULNERABILITIES = 'delete_vulnerabilities',

  // Scanner Management
  MANAGE_SCANNERS = 'manage_scanners',
  DELETE_SCANNERS = 'delete_scanners',

  // Remediation Management
  MANAGE_REMEDIATION = 'manage_remediation',
  DELETE_REMEDIATION = 'delete_remediation',

  // Compliance Features
  VIEW_COMPLIANCE = 'view_compliance',
  MANAGE_COMPLIANCE = 'manage_compliance',
  VIEW_AUDIT_LOGS = 'view_audit_logs',
  MANAGE_INCIDENTS = 'manage_incidents',

  // Support & Tickets
  MANAGE_SUPPORT_TICKETS = 'manage_support_tickets',
  DELETE_SUPPORT_TICKETS = 'delete_support_tickets',

  // Sensitive Operations
  VIEW_ENCRYPTION_KEYS = 'view_encryption_keys',
  MANAGE_PAYMENT = 'manage_payment',
  VIEW_CERTIFICATES = 'view_certificates',

  // Super Admin Only
  CROSS_COMPANY_ACCESS = 'cross_company_access',
  MANAGE_ALL_COMPANIES = 'manage_all_companies',
}

/**
 * Role hierarchy with privilege levels
 * Lower number = higher privilege
 *
 * `auditor` and `manager` sit BELOW `user` on purpose (#784). They are not
 * company-administration roles: an auditor's access is the Audit Hub, reached
 * through its own checks, and nothing about auditing a tenant implies the right
 * to administer it. They are listed explicitly rather than left to the
 * unknown-role fallback so the ranking is a decision somebody made and can find,
 * instead of a side effect of a missing key.
 */
const ROLE_HIERARCHY: Record<string, number> = {
  super_admin: 0,
  admin: 1,
  compliance_officer: 2,
  user: 3,
  manager: 4,
  auditor: 5,
};

/**
 * Permission matrix: role -> permissions[]
 * Defines what each role can do
 */
const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  super_admin: [
    // Super admin has ALL permissions
    ...Object.values(Permission),
  ],

  admin: [
    // Company management
    Permission.MANAGE_COMPANY,

    // User management
    Permission.MANAGE_USERS,
    Permission.INVITE_USERS,
    Permission.DELETE_USERS,

    // Scan management
    Permission.MANAGE_SCANS,
    Permission.RUN_SCANS,
    Permission.DELETE_SCANS,

    // Asset management
    Permission.MANAGE_ASSETS,
    Permission.DELETE_ASSETS,

    // Vulnerability management
    Permission.MANAGE_VULNERABILITIES,
    Permission.DELETE_VULNERABILITIES,

    // Scanner management
    Permission.MANAGE_SCANNERS,
    Permission.DELETE_SCANNERS,

    // Remediation management
    Permission.MANAGE_REMEDIATION,
    Permission.DELETE_REMEDIATION,

    // Compliance features
    Permission.VIEW_COMPLIANCE,
    Permission.MANAGE_COMPLIANCE,
    Permission.VIEW_AUDIT_LOGS,
    Permission.MANAGE_INCIDENTS,

    // Support
    Permission.MANAGE_SUPPORT_TICKETS,
    Permission.DELETE_SUPPORT_TICKETS,

    // Sensitive operations
    Permission.VIEW_ENCRYPTION_KEYS,
    Permission.MANAGE_PAYMENT,
    Permission.VIEW_CERTIFICATES,
  ],

  compliance_officer: [
    // Read-only company
    // User management (limited)
    Permission.MANAGE_USERS,
    Permission.INVITE_USERS,

    // Scan viewing
    Permission.MANAGE_SCANS,
    Permission.RUN_SCANS,

    // Asset viewing
    Permission.MANAGE_ASSETS,

    // Vulnerability viewing
    Permission.MANAGE_VULNERABILITIES,

    // Remediation management
    Permission.MANAGE_REMEDIATION,

    // Full compliance access
    Permission.VIEW_COMPLIANCE,
    Permission.MANAGE_COMPLIANCE,
    Permission.VIEW_AUDIT_LOGS,
    Permission.MANAGE_INCIDENTS,

    // Support
    Permission.MANAGE_SUPPORT_TICKETS,
  ],

  user: [
    // Basic scan access
    Permission.RUN_SCANS,

    // View assets
    Permission.MANAGE_ASSETS,

    // View vulnerabilities
    Permission.MANAGE_VULNERABILITIES,

    // Participate in remediation
    Permission.MANAGE_REMEDIATION,

    // View compliance (read-only)
    Permission.VIEW_COMPLIANCE,

    // Create support tickets
    Permission.MANAGE_SUPPORT_TICKETS,
  ],
};

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private authService = inject(AuthService);

  constructor() {}

  /**
   * Get current user from auth service
   */
  private getCurrentUser(): User | undefined {
    return this.authService.userData;
  }

  /**
   * The role every check should resolve against (#784).
   *
   * A platform account's grant lives in the `adminRole` claim the admin backend
   * issues, while `role` carries whatever that account holds *inside the current
   * tenant* — for a dual account, deliberately something narrow such as
   * 'auditor'. Reading `role` alone therefore denied the accounts that
   * administer the platform, including the only ones able to assign roles at
   * all. `firestore.rules` has always honoured both claims; this brings the app
   * in line rather than granting anything new: `super_admin` already holds every
   * permission in the matrix below.
   */
  private effectiveRole(user: User | null | undefined): string | undefined {
    if (!user) return undefined;
    if (user.adminRole === 'super_admin') return 'super_admin';
    return user.role;
  }

  /**
   * Get current user as observable
   */
  private getCurrentUserObservable(): Observable<User | null> {
    return this.authService.currentUser;
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: string): boolean {
    const effective = this.effectiveRole(this.getCurrentUser());
    return !!effective && effective === role;
  }

  /**
   * Check if user has a specific role (observable)
   */
  hasRoleObservable(role: string): Observable<boolean> {
    return this.getCurrentUserObservable().pipe(
      map(user => {
        const effective = this.effectiveRole(user);
        if (!effective) {
          return false;
        }
        return effective === role;
      })
    );
  }

  /**
   * Check if user is super admin
   */
  isSuperAdmin(): boolean {
    return this.hasRole('super_admin');
  }

  /**
   * Check if user is super admin (observable)
   */
  isSuperAdminObservable(): Observable<boolean> {
    return this.hasRoleObservable('super_admin');
  }

  /**
   * Check if user is admin (in their current company)
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Check if user is admin (observable)
   */
  isAdminObservable(): Observable<boolean> {
    return this.hasRoleObservable('admin');
  }

  /**
   * Check if user is compliance officer
   */
  isComplianceOfficer(): boolean {
    return this.hasRole('compliance_officer');
  }

  /**
   * Check if user is compliance officer (observable)
   */
  isComplianceOfficerObservable(): Observable<boolean> {
    return this.hasRoleObservable('compliance_officer');
  }

  /**
   * Check if user is admin or compliance officer
   */
  isAdminOrCompliance(): boolean {
    return this.isAdmin() || this.isComplianceOfficer();
  }

  /**
   * Check if user is admin or compliance officer (observable)
   */
  isAdminOrComplianceObservable(): Observable<boolean> {
    return combineLatest([
      this.isAdminObservable(),
      this.isComplianceOfficerObservable(),
    ]).pipe(
      map(([isAdmin, isCompliance]) => isAdmin || isCompliance)
    );
  }

  /**
   * Check if user has at least the specified role level
   * (e.g., hasRoleLevel('admin') returns true for super_admin and admin)
   */
  hasRoleLevel(minimumRole: string): boolean {
    const effective = this.effectiveRole(this.getCurrentUser());
    if (!effective) {
      return false;
    }

    const userLevel = ROLE_HIERARCHY[effective];
    const requiredLevel = ROLE_HIERARCHY[minimumRole];

    // Lower number = higher privilege
    // If user level is undefined, treat as lowest privilege (999)
    return (userLevel ?? 999) <= (requiredLevel ?? 999);
  }

  /**
   * Check if user has at least the specified role level (observable)
   */
  hasRoleLevelObservable(minimumRole: string): Observable<boolean> {
    return this.getCurrentUserObservable().pipe(
      map(user => {
        const effective = this.effectiveRole(user);
        if (!effective) {
          return false;
        }

        const userLevel = ROLE_HIERARCHY[effective];
        const requiredLevel = ROLE_HIERARCHY[minimumRole];

        return (userLevel ?? 999) <= (requiredLevel ?? 999);
      })
    );
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: Permission): boolean {
    const effective = this.effectiveRole(this.getCurrentUser());
    if (!effective) {
      return false;
    }

    const rolePermissions = ROLE_PERMISSIONS[effective] || [];
    return rolePermissions.includes(permission);
  }

  /**
   * Check if user has a specific permission (observable)
   */
  hasPermissionObservable(permission: Permission): Observable<boolean> {
    return this.getCurrentUserObservable().pipe(
      map(user => {
        const effective = this.effectiveRole(user);
        if (!effective) {
          return false;
        }

        const rolePermissions = ROLE_PERMISSIONS[effective] || [];
        return rolePermissions.includes(permission);
      })
    );
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has any of the specified permissions (observable)
   */
  hasAnyPermissionObservable(permissions: Permission[]): Observable<boolean> {
    return this.getCurrentUserObservable().pipe(
      map(user => {
        const effective = this.effectiveRole(user);
        if (!effective) {
          return false;
        }

        const rolePermissions = ROLE_PERMISSIONS[effective] || [];
        return permissions.some(permission => rolePermissions.includes(permission));
      })
    );
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has all of the specified permissions (observable)
   */
  hasAllPermissionsObservable(permissions: Permission[]): Observable<boolean> {
    return this.getCurrentUserObservable().pipe(
      map(user => {
        const effective = this.effectiveRole(user);
        if (!effective) {
          return false;
        }

        const rolePermissions = ROLE_PERMISSIONS[effective] || [];
        return permissions.every(permission => rolePermissions.includes(permission));
      })
    );
  }

  /**
   * Check if user can access a specific company
   * Super admin can access all companies
   * Other users can only access companies they belong to
   */
  canAccessCompany(companyId: string): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }

    // Super admin can access any company
    if (this.isSuperAdmin()) {
      return true;
    }

    // Check if user belongs to the company
    return (
      user.currentCompanyId === companyId ||
      (user.companies && user.companies.includes(companyId))
    );
  }

  /**
   * Check if user can access a specific company (observable)
   */
  canAccessCompanyObservable(companyId: string): Observable<boolean> {
    return this.getCurrentUserObservable().pipe(
      map(user => {
        if (!user) {
          return false;
        }

        // Super admin can access any company
        if (this.effectiveRole(user) === 'super_admin') {
          return true;
        }

        // Check if user belongs to the company
        return (
          user.currentCompanyId === companyId ||
          (user.companies && user.companies.includes(companyId))
        );
      })
    );
  }

  /**
   * Get all permissions for current user
   */
  getUserPermissions(): Permission[] {
    const user = this.getCurrentUser();
    const effective = this.effectiveRole(user);
    if (!effective) {
      return [];
    }

    return ROLE_PERMISSIONS[effective] || [];
  }

  /**
   * Get all permissions for current user (observable)
   */
  getUserPermissionsObservable(): Observable<Permission[]> {
    return this.getCurrentUserObservable().pipe(
      map(user => {
        const effective = this.effectiveRole(user);
        if (!effective) {
          return [];
        }

        return ROLE_PERMISSIONS[effective] || [];
      })
    );
  }

  /**
   * Get user's role for a specific company
   * Returns undefined if user doesn't belong to the company
   */
  getRoleForCompany(companyId: string): string | undefined {
    const user = this.getCurrentUser();
    if (!user) {
      return undefined;
    }

    // Super admin always has super_admin role
    if (this.isSuperAdmin()) {
      return 'super_admin';
    }

    // Check company-specific role
    if (user.companyRoles && user.companyRoles[companyId]) {
      return user.companyRoles[companyId];
    }

    // If user belongs to company but no specific role set, use global role
    if (
      user.currentCompanyId === companyId ||
      (user.companies && user.companies.includes(companyId))
    ) {
      return user.role || 'user';
    }

    return undefined;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authService.isLoggedIn;
  }

  /**
   * Check if user is authenticated (observable)
   */
  isAuthenticatedObservable(): Observable<boolean> {
    return this.getCurrentUserObservable().pipe(
      map(user => user !== null && user !== undefined)
    );
  }
}
